class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Fetches the live shipping rate only once the drawer is open, so page loads stay request-free.
class CartShippingEstimate extends HTMLElement {
  static cacheLifetime = 30 * 60 * 1000;
  static pending = new Map();
  static tracked = new Set();

  connectedCallback() {
    this.drawer = this.closest('cart-drawer');
    if (!this.drawer || !this.dataset.zip) return;

    if (this.drawer.classList.contains('active')) {
      this.estimate();
      return;
    }

    this.observer = new MutationObserver(() => {
      if (!this.drawer.classList.contains('active')) return;
      this.observer.disconnect();
      this.estimate();
    });
    this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  async estimate() {
    const cacheKey = `cart-shipping:${this.dataset.key}`;
    let price = this.readCache(cacheKey);

    if (price === null) {
      // The drawer can re-render twice for one cart change; both renders share one request.
      if (!CartShippingEstimate.pending.has(cacheKey)) {
        CartShippingEstimate.pending.set(
          cacheKey,
          this.fetchPrice().finally(() => CartShippingEstimate.pending.delete(cacheKey))
        );
      }
      price = await CartShippingEstimate.pending.get(cacheKey);
      // A re-render during the request replaced this element; its successor reports the current cart.
      if (!this.isConnected) return;
      // Without a usable rate the server-rendered "calculated at checkout" text stays.
      if (price === null) return this.track(null);
      this.writeCache(cacheKey, price);
    }

    this.render(price);
    this.track(price);
  }

  async fetchPrice() {
    const params = new URLSearchParams({
      'shipping_address[country]': this.dataset.country,
      'shipping_address[zip]': this.dataset.zip,
    });
    const response = await fetch(`${routes.cart_url}/shipping_rates.json?${params}`).catch(() => null);
    if (!response || !response.ok) return null;

    const { shipping_rates: rates } = await response.json();
    if (!rates || rates.length === 0) return null;

    return Math.min(...rates.map((rate) => Math.round(parseFloat(rate.price) * 100)));
  }

  render(price) {
    const isFree = price === 0;
    const nudge = this.querySelector('[data-shipping-nudge]');
    const total = this.closest('.cart-drawer__footer').querySelector('.totals__total-value');

    this.querySelector('[data-shipping-value]').textContent = isFree ? this.dataset.freeLabel : this.formatMoney(price);
    this.querySelector('[data-shipping-free]').hidden = !isFree;
    if (nudge) nudge.hidden = isFree;
    if (total) total.textContent = this.formatMoney(Number(this.dataset.cartTotal) + price);
  }

  // Reports what the shipping summary showed to GA4, once per cart state.
  track(price) {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || !privacy.analyticsProcessingAllowed || !privacy.analyticsProcessingAllowed()) return;
    if (window.Shopify.designMode || CartShippingEstimate.tracked.has(this.dataset.key)) return;
    CartShippingEstimate.tracked.add(this.dataset.key);

    const params = { send_to: 'G-ETNNY3YE8S' };
    if (price !== null) {
      params.shipping_cost = price / 100;
      params.free_shipping_reached = price === 0;
    }
    if (this.dataset.freeRemaining) params.free_shipping_remaining = Number(this.dataset.freeRemaining) / 100;

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    gtag('event', 'cart_shipping_info', params);
  }

  formatMoney(cents) {
    const [whole, decimals] = (cents / 100).toFixed(2).split('.');
    const group = (separator) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    const amounts = {
      amount: `${group(',')}.${decimals}`,
      amount_no_decimals: group(','),
      amount_with_comma_separator: `${group('.')},${decimals}`,
      amount_no_decimals_with_comma_separator: group('.'),
    };

    return this.dataset.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => amounts[key] || match);
  }

  readCache(key) {
    try {
      const entry = JSON.parse(sessionStorage.getItem(key));
      if (!entry || Date.now() - entry.storedAt > CartShippingEstimate.cacheLifetime) return null;
      return entry.price;
    } catch (error) {
      return null;
    }
  }

  writeCache(key, price) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ price, storedAt: Date.now() }));
    } catch (error) {
      // Storage can be unavailable (private mode); the rate is then fetched again next time.
    }
  }
}

customElements.define('cart-shipping-estimate', CartShippingEstimate);
